import React, { useEffect, useRef } from "react";
import { Image } from "primereact/image";
import { toast } from "react-toastify";
import { APIResponseEntity } from "../../../../interface/api-responce";
import { APIUploadReceiverImages } from "../../../../service/api-service";
import { albumsFileExtension, fileExtensionNotAllowed, fileSizeExceeds, toastOptObj } from "../../../../utils/constants";

// no limit on how many photos can be attached; each photo has to be an image of less than 25 MB
const RECEIVER_IMAGE_MAX_BYTES = 25 * 1024 * 1024;

export interface ReceiverImageItem {
    id: string;
    file: File;
    /** local preview of the picked file */
    previewUrl: string;
    /** set once the file is on the server; a retried submit does not upload it again */
    uploadedUrl?: string;
}

interface ReceiverImagesUploadProps {
    title: string;
    items: ReceiverImageItem[];
    onChange: (items: ReceiverImageItem[]) => void;
}

/**
 * Uploads the photos that are not on the server yet and returns every item with its url (the same list, in the same order),
 * or null when the upload failed (the reason is already shown).
 */
export const uploadPendingReceiverImages = async (tbsId: string, items: ReceiverImageItem[]): Promise<ReceiverImageItem[] | null> => {
    const pending = items.filter((item) => !item.uploadedUrl);
    if (pending.length === 0) {
        return items;
    }

    let resp: Awaited<ReturnType<typeof APIUploadReceiverImages>> | undefined;
    try {
        resp = await APIUploadReceiverImages(tbsId, pending.map((item) => item.file));
    } catch (error) {
        // this call has no global error toast, so the server's reason (wrong file type, too large, no permission) is shown here
        const errorData: APIResponseEntity | undefined = (error as { response?: { data?: APIResponseEntity } })?.response?.data;
        toast.error(errorData?.message || errorData?.errorMsg || "The images could not be uploaded. Please try again.", { ...toastOptObj });
        return null;
    }
    const apiResp: APIResponseEntity | undefined = resp?.data;
    if (resp && resp.status === 200 && apiResp && apiResp.status && apiResp.statusCode === 200
        && Array.isArray(apiResp.data?.urls) && apiResp.data.urls.length === pending.length) {
        const urlById = new Map<string, string>(pending.map((item, index) => [item.id, apiResp.data.urls[index]]));
        return items.map((item) => ({ ...item, uploadedUrl: item.uploadedUrl ?? urlById.get(item.id) }));
    }

    toast.error(apiResp?.message || apiResp?.errorMsg || "The images could not be uploaded. Please try again.", { ...toastOptObj });
    return null;
};

/** Optional photos of the received / damaged / returned items: pick several at once, see them, remove any before submitting. */
const ReceiverImagesUpload: React.FC<ReceiverImagesUploadProps> = ({ title, items, onChange }) => {
    // the previews are blob urls: let go of them when the page goes
    const itemsRef = useRef<ReceiverImageItem[]>(items);
    itemsRef.current = items;
    useEffect(() => () => itemsRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl)), []);

    const handlePick = (event: React.ChangeEvent<HTMLInputElement>) => {
        const picked = Array.from(event.target.files ?? []);
        event.target.value = ""; // the same photo can be picked again after it was removed
        if (picked.length === 0) { return; }

        const next = [...items];
        for (const file of picked) {
            const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
            if (!albumsFileExtension.includes(extension)) {
                toast.error(`${file.name}: ${fileExtensionNotAllowed}`, { ...toastOptObj });
                continue;
            }
            if (file.size >= RECEIVER_IMAGE_MAX_BYTES) {
                toast.error(`${file.name}: ${fileSizeExceeds}`, { ...toastOptObj });
                continue;
            }
            next.push({ id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, file, previewUrl: URL.createObjectURL(file) });
        }
        onChange(next);
    };

    const handleRemove = (id: string) => {
        const removed = items.find((item) => item.id === id);
        if (removed) { URL.revokeObjectURL(removed.previewUrl); }
        onChange(items.filter((item) => item.id !== id));
    };

    return (
        <div className="form-group" data-testid="receiver-images">
            <label className="d-block mb-2">{title}</label>
            <input
                type="file"
                className="form-control form-control-lg"
                multiple
                accept={albumsFileExtension.map((extension) => `.${extension}`).join(",")}
                onChange={handlePick}
                aria-label={title}
            />
            <small className="text-muted d-block mt-1">
                Optional. Select as many images as you need. JPG, JPEG, PNG, GIF or WEBP, less than 25 MB each.{items.length > 0 ? ` ${items.length} selected.` : ""}
            </small>

            {items.length > 0 && (
                <div className="d-flex flex-wrap gap-3 mt-3">
                    {items.map((item) => (
                        <div key={item.id} className="text-center" style={{ width: "100px" }}>
                            <Image src={item.previewUrl} zoomSrc={item.previewUrl} preview width="100" height="75" alt={item.file.name} className="file-card-image" />
                            <div className="text-truncate small" title={item.file.name}>{item.file.name}</div>
                            <button
                                type="button"
                                className="btn btn-link btn-sm text-danger p-0"
                                onClick={() => handleRemove(item.id)}
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ReceiverImagesUpload;
